import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { TwoFactorSection } from "./TwoFactorSection";
import { KeyRound, User } from "lucide-react";

export function AccountTab() {
  const { user, formatApiErrorDetail } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const changePassword = async () => {
    if (!currentPassword || !newPassword) return toast.error("Fill in your current and new password");
    if (newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-account-tab" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl mb-1">Account</h2>
        <p className="text-sm text-[#f1e0c5]/50">Manage your login and security.</p>
      </div>

      <div className="rounded-2xl bg-black/40 border border-white/10 p-6 space-y-2">
        <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#f1e0c5]/40"><User className="w-3.5 h-3.5" /> Signed In As</p>
        <p data-testid="settings-account-user-name" className="font-display text-lg">{user?.name}</p>
        <p data-testid="settings-account-user-email" className="text-sm text-[#f1e0c5]/50">{user?.email}</p>
      </div>

      {!user?.is_guest && (
        <div id="settings-section-password" className="rounded-2xl bg-black/40 border border-white/10 p-6 space-y-4">
          <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#f1e0c5]/40"><KeyRound className="w-3.5 h-3.5" /> Change Password</p>
          <Input
            data-testid="settings-current-password-input"
            type="password"
            placeholder="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="bg-black/30 border-white/10"
          />
          <Input
            data-testid="settings-new-password-input"
            type="password"
            placeholder="New password (min. 8 characters)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="bg-black/30 border-white/10"
          />
          <Input
            data-testid="settings-confirm-password-input"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && changePassword()}
            className="bg-black/30 border-white/10"
          />
          <Button data-testid="settings-change-password-submit" onClick={changePassword} disabled={busy} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">
            {busy ? "Updating…" : "Update Password"}
          </Button>
        </div>
      )}

      {!user?.is_guest && <TwoFactorSection />}
    </div>
  );
}
